import React, { useState } from 'react';
import { Calculator, Copy, Trash2, Users, DollarSign, Info, TrendingDown } from 'lucide-react';

const CacCalculatorTool = () => {
  const [marketingSpend, setMarketingSpend] = useState('');
  const [customersAcquired, setCustomersAcquired] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState('');

  const calculate = () => {
    const spend = parseFloat(marketingSpend);
    const customers = parseFloat(customersAcquired);

    if (isNaN(spend) || isNaN(customers) || spend < 0 || customers <= 0) {
      setResult(null);
      return;
    }

    // CAC = Total Marketing Spend / Number of Customers Acquired
    const cac = spend / customers;
    const customersPerDollar = customers / spend;

    setResult({
      marketingSpend: spend,
      customersAcquired: customers,
      cac,
      customersPerDollar,
      costPer100Customers: cac * 100
    });
  };

  const copyResult = (value, label) => {
    navigator.clipboard.writeText(value.toFixed(2));
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const clearAll = () => {
    setMarketingSpend('');
    setCustomersAcquired('');
    setResult(null);
  };

  const formatCurrency = (value) => {
    return '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatNumber = (value) => {
    return value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  // Determine CAC rating (general guidelines)
  const getCacRating = (cac) => {
    if (cac < 50) return { label: 'Excellent', color: 'text-green-400' };
    if (cac < 100) return { label: 'Good', color: 'text-blue-400' };
    if (cac < 200) return { label: 'Average', color: 'text-yellow-400' };
    if (cac < 500) return { label: 'Above Average', color: 'text-orange-400' };
    return { label: 'High', color: 'text-red-400' };
  };

  const benchmarks = [
    { industry: 'E-commerce', range: '$10 - $50' },
    { industry: 'SaaS (B2B)', range: '$200 - $500' },
    { industry: 'SaaS (B2C)', range: '$50 - $150' },
    { industry: 'Financial Services', range: '$175 - $400' },
    { industry: 'Travel', range: '$50 - $150' },
    { industry: 'Retail', range: '$10 - $50' },
    { industry: 'Education', range: '$50 - $200' },
    { industry: 'Healthcare', range: '$200 - $500' },
  ];

  return (
    <div className="space-y-6">
      {/* Input Section */}
      <div className="p-4 bg-gray-800 rounded-lg space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <DollarSign size={18} className="text-green-400" />
          <h3 className="text-sm font-medium text-gray-300">Marketing Data</h3>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Total Marketing Spend ($)
            </label>
            <input
              type="number"
              step="0.01"
              value={marketingSpend}
              onChange={(e) => setMarketingSpend(e.target.value)}
              placeholder="e.g., 10000"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="marketing-spend-input"
            />
            <p className="text-xs text-gray-500 mt-1">Include all marketing & sales costs</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Customers Acquired
            </label>
            <input
              type="number"
              value={customersAcquired}
              onChange={(e) => setCustomersAcquired(e.target.value)}
              placeholder="e.g., 100"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="customers-input"
            />
            <p className="text-xs text-gray-500 mt-1">New paying customers in the period</p>
          </div>
        </div>
      </div>

      {/* Calculate Button */}
      <button
        onClick={calculate}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
        data-testid="calculate-cac-btn"
      >
        <Calculator size={18} />
        Calculate CAC
      </button>

      {/* Results Section */}
      {result && (
        <div className="p-4 bg-gray-800 rounded-lg space-y-4" data-testid="cac-results">
          {/* Main Result */}
          <div className="p-4 bg-gray-900 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Users size={18} className="text-blue-400" />
                <span className="text-sm text-gray-400">Customer Acquisition Cost (CAC)</span>
              </div>
              <button
                onClick={() => copyResult(result.cac, 'cac')}
                className={`p-1.5 rounded transition-colors ${
                  copied === 'cac' ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                }`}
                data-testid="copy-cac"
              >
                <Copy size={14} />
              </button>
            </div>
            <div className="text-4xl font-bold text-green-400" data-testid="cac-result">
              {formatCurrency(result.cac)}
            </div>
            <div className={`text-sm mt-1 ${getCacRating(result.cac).color}`}>
              {getCacRating(result.cac).label} CAC
            </div>
          </div>

          {/* Secondary Metrics */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-3 bg-gray-900 rounded-lg">
              <div className="text-sm text-gray-400 mb-1">Cost per 100 Customers</div>
              <div className="text-lg font-bold text-purple-400">
                {formatCurrency(result.costPer100Customers)}
              </div>
            </div>
            <div className="p-3 bg-gray-900 rounded-lg">
              <div className="text-sm text-gray-400 mb-1">Customers per $1,000 Spend</div>
              <div className="text-lg font-bold text-blue-400">
                {(result.customersPerDollar * 1000).toFixed(1)} customers
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="border-t border-gray-700 pt-4">
            <h4 className="text-sm font-medium text-gray-300 mb-3">Calculation Summary</h4>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Total Marketing Spend</span>
                <span className="text-white font-medium">{formatCurrency(result.marketingSpend)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Customers Acquired</span>
                <span className="text-white font-medium">{formatNumber(result.customersAcquired)}</span>
              </div>
              <div className="flex justify-between text-sm border-t border-gray-700 pt-2 mt-2">
                <span className="text-gray-400">CAC (Spend ÷ Customers)</span>
                <span className="text-green-400 font-medium">{formatCurrency(result.cac)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* What is CAC */}
      <div className="p-4 bg-gray-800 rounded-lg">
        <div className="flex items-center gap-2 mb-3">
          <Info size={18} className="text-blue-400" />
          <h3 className="text-sm font-medium text-gray-300">What is CAC?</h3>
        </div>
        <div className="space-y-3 text-sm text-gray-400">
          <p>
            <span className="text-white font-medium">Customer Acquisition Cost (CAC)</span> measures how much 
            it costs your business to acquire a new customer. It includes all marketing and sales expenses 
            divided by the number of new customers gained in that period.
          </p>
          <div className="p-3 bg-gray-900 rounded">
            <div className="text-xs text-gray-500 mb-2">Formula:</div>
            <div className="font-mono text-yellow-400">
              CAC = Total Marketing & Sales Spend ÷ New Customers Acquired
            </div>
          </div>
          <p>
            <span className="text-white font-medium">What to include in spend:</span> Advertising costs, 
            marketing team salaries, sales team costs, software/tools, agency fees, content creation, 
            and any other customer acquisition-related expenses.
          </p>
        </div>
      </div>

      {/* Industry Benchmarks */}
      <div className="p-4 bg-gray-800 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-4">Industry CAC Benchmarks</h3>
        <div className="grid md:grid-cols-2 gap-3">
          {benchmarks.map((item, index) => (
            <div key={index} className="flex justify-between items-center p-2 bg-gray-900 rounded">
              <span className="text-sm text-gray-400">{item.industry}</span>
              <span className="text-sm font-medium text-green-400">{item.range}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-3">
          Note: CAC varies significantly based on business model, target market, and growth stage. 
          Early-stage companies often have higher CAC. Focus on your CAC to LTV ratio.
        </p>
      </div>

      {/* CAC to LTV Ratio */}
      <div className="p-4 bg-gray-800 rounded-lg">
        <div className="flex items-center gap-2 mb-3">
          <TrendingDown size={18} className="text-green-400" />
          <h3 className="text-sm font-medium text-gray-300">CAC to LTV Ratio</h3>
        </div>
        <div className="space-y-3 text-sm text-gray-400">
          <p>
            The <span className="text-white font-medium">CAC to LTV (Lifetime Value) ratio</span> is critical 
            for measuring business health. It tells you if you're spending too much to acquire customers 
            relative to what they're worth.
          </p>
          <div className="grid md:grid-cols-3 gap-3">
            <div className="p-2 bg-gray-900 rounded text-center">
              <div className="text-lg font-bold text-red-400">1:1</div>
              <div className="text-xs text-gray-500">Breaking even</div>
            </div>
            <div className="p-2 bg-gray-900 rounded text-center">
              <div className="text-lg font-bold text-yellow-400">3:1</div>
              <div className="text-xs text-gray-500">Healthy ratio</div>
            </div>
            <div className="p-2 bg-gray-900 rounded text-center">
              <div className="text-lg font-bold text-green-400">5:1+</div>
              <div className="text-xs text-gray-500">Excellent</div>
            </div>
          </div>
          <p className="text-xs">
            A 3:1 LTV:CAC ratio means your customer is worth 3x what you paid to acquire them. 
            If your ratio is below 3:1, consider optimizing marketing channels or improving retention.
          </p>
        </div>
      </div>

      {/* Tips to Reduce CAC */}
      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">Tips to Reduce CAC</h3>
        <div className="text-sm text-gray-400 space-y-1">
          <div>• <span className="text-white">Optimize ad targeting</span> - Focus on high-converting audiences</div>
          <div>• <span className="text-white">Improve conversion rates</span> - Better landing pages and funnels</div>
          <div>• <span className="text-white">Leverage organic channels</span> - SEO, content, social media</div>
          <div>• <span className="text-white">Implement referral programs</span> - Let customers bring customers</div>
          <div>• <span className="text-white">Reduce churn</span> - Keeping customers lowers effective CAC</div>
        </div>
      </div>

      {/* Clear Button */}
      <button
        onClick={clearAll}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        data-testid="clear-cac-btn"
      >
        <Trash2 size={16} />
        Clear All
      </button>
    </div>
  );
};

export default CacCalculatorTool;
